/**
 * Notification type labels and categories
 * Used by both web and mobile for display and preferences
 */

import { NotificationType } from '../types/notification';

export interface NotificationTypeConfig {
  type: NotificationType;
  label: string;
  description: string;
  category: 'communication' | 'workflow' | 'updates' | 'system';
  defaultPush: boolean;
  defaultEmail: boolean;
  defaultInApp: boolean;
}

export const NOTIFICATION_TYPE_CONFIGS: NotificationTypeConfig[] = [
  {
    type: NotificationType.COMMENT,
    label: 'Comments',
    description: 'When someone comments on your OKR',
    category: 'communication',
    defaultPush: true,
    defaultEmail: false,
    defaultInApp: true,
  },
  {
    type: NotificationType.REPLY,
    label: 'Replies',
    description: 'When someone replies to your comment',
    category: 'communication',
    defaultPush: true,
    defaultEmail: false,
    defaultInApp: true,
  },
  {
    type: NotificationType.MENTION,
    label: 'Mentions',
    description: 'When you are mentioned in a comment',
    category: 'communication',
    defaultPush: true,
    defaultEmail: false,
    defaultInApp: true,
  },
  {
    type: NotificationType.APPROVAL_REQUEST,
    label: 'Approval Requests',
    description: 'When an OKR is submitted for your approval',
    category: 'workflow',
    defaultPush: true,
    defaultEmail: true,
    defaultInApp: true,
  },
  {
    type: NotificationType.APPROVAL_APPROVED,
    label: 'Approvals',
    description: 'When your OKR is approved',
    category: 'workflow',
    defaultPush: true,
    defaultEmail: false,
    defaultInApp: true,
  },
  {
    type: NotificationType.APPROVAL_REJECTED,
    label: 'Rejections',
    description: 'When your OKR is rejected',
    category: 'workflow',
    defaultPush: true,
    defaultEmail: true,
    defaultInApp: true,
  },
  {
    type: NotificationType.OKR_ASSIGNED,
    label: 'OKR Assignments',
    description: 'When an OKR is assigned to you',
    category: 'updates',
    defaultPush: true,
    defaultEmail: false,
    defaultInApp: true,
  },
  {
    type: NotificationType.PROGRESS_UPDATE,
    label: 'Progress Updates',
    description: 'When progress is updated on your OKRs',
    category: 'updates',
    defaultPush: false,
    defaultEmail: false,
    defaultInApp: true,
  },
  {
    type: NotificationType.CHECK_IN_REMINDER,
    label: 'Check-in Reminders',
    description: 'Reminders for upcoming or overdue check-ins',
    category: 'system',
    defaultPush: true,
    defaultEmail: true,
    defaultInApp: true,
  },
  {
    type: NotificationType.CHECK_IN_SUBMITTED,
    label: 'Check-in Submissions',
    description: 'When a team member submits a check-in on their OKR',
    category: 'workflow',
    defaultPush: true,
    defaultEmail: true,
    defaultInApp: true,
  },
  {
    type: NotificationType.SYSTEM,
    label: 'System Notifications',
    description: 'System updates and announcements',
    category: 'system',
    defaultPush: true,
    defaultEmail: false,
    defaultInApp: true,
  },
];

/**
 * Get config for a specific notification type
 */
export function getNotificationTypeConfig(type: NotificationType): NotificationTypeConfig | undefined {
  return NOTIFICATION_TYPE_CONFIGS.find(config => config.type === type);
}
